import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const locale = "sr";

export const studentsManagement = createApi({
    reducerPath: "studentsManagement",
    baseQuery: fetchBaseQuery({
        baseUrl: `${BASE_URL}`,
        prepareHeaders: (headers) => {
            const jwtToken = localStorage.getItem("userToken") || sessionStorage.getItem("userToken");

            if (jwtToken) {
                headers.set("authorization", `Bearer ${jwtToken}`);
            }

            return headers;
        },
    }),
    endpoints: (builder) => ({
        studentsList: builder.mutation({
            query: (body) => {
                let url = `v1/${locale}/students`;

                for (const property in body) {
                    if ((property && body[property]) || (property === "startPage" && body[property] === 0)) {
                        if (url?.includes("?")) {
                            url += `&${property}=${body[property]}`;
                        } else {
                            url += `?${property}=${body[property]}`;
                        }
                    }
                }

                return {
                    url: url,
                    method: "GET",
                };
            },
        }),
        viewStudents: builder.mutation({
            query: (studentId) => ({
                url: `v1/${locale}/students/${studentId}`,
                method: "GET",
            }),
        }),

        addStudents: builder.mutation({
            query: (body) => ({
                url: `v1/${locale}/students`,
                method: "POST",
                body: body,
            }),
        }),

        updateStudents: builder.mutation({
            query: ({ body, studentId }) => ({
                url: `v1/${locale}/students/${studentId}`,
                method: "PATCH",
                body: body,
            }),
        }),

        deleteStudents: builder.mutation({
            query: (studentId) => ({
                url: `v1/${locale}/students/${studentId}`,
                method: "DELETE",
            }),
        }),

        addAvatar: builder.mutation({
            query: ({ file, studentId }) => {
                let formData = new FormData();
                formData.append("file", file);

                return {
                    url: `v1/${locale}/students/${studentId}/avatar`,
                    method: "POST",
                    body: formData
                }
            },
        }),

        viewAvatar: builder.mutation({
            query: (studentId) => ({
                url: `v1/${locale}/students/${studentId}/avatar`,
                method: "GET",
            }),
        }),

        deleteAvatar: builder.mutation({
            query: (studentId) => ({
                url: `v1/${locale}/students/${studentId}/avatar`,
                method: "DELETE",
            }),
        }),
        addDocument: builder.mutation({
            query: ({ file, studentId }) => {
                let formData = new FormData();
                formData.append("file", file);

                return {
                    url: `v1/${locale}/students/${studentId}/documents`,
                    method: "POST",
                    body: formData
                }
            },
        }),
        listDocuments: builder.mutation({
            query: (studentId) => ({
                url: `v1/${locale}/students/${studentId}/documents`,
                method: "GET",
            }),
        }),
        deleteDocument: builder.mutation({
            query: ({studentId, documentId}) => ({
                url: `v1/${locale}/students/${studentId}/documents/${documentId}`,
                method: "DELETE",
            }),
        }),
        downloadDocument: builder.mutation({
            query: ({studentId, documentId}) => ({
                url: `v1/${locale}/students/${studentId}/documents/${documentId}`,
                method: "GET",
                responseHandler: (response) => response.blob(),
            }),
        }),
        notesList: builder.mutation({
            query: (studentId) => ({
                url: `v1/${locale}/students/${studentId}/notes`,
                method: "GET",
            }),
        }),
        viewNote: builder.mutation({
            query: ({ studentId, noteId }) => ({
                url: `v1/${locale}/students/${studentId}/notes/${noteId}`,
                method: "GET",
            }),
        }),
        addNote: builder.mutation({
            query: ({ body, studentId }) => ({
                url: `v1/${locale}/students/${studentId}/notes`,
                method: "POST",
                body: body,
            }),
        }),
        deleteNote: builder.mutation({
            query: ({ studentId, noteId }) => ({
                url: `v1/${locale}/students/${studentId}/notes/${noteId}`,
                method: "DELETE",
            }),
        }),
        testsList: builder.mutation({
            query: ({ studentId }) => {
                let url = `v1/${locale}/students/${studentId}/tests`;

                return {
                    url: url,
                    method: "GET",
                };
            },
        }),
        viewTest: builder.mutation({
            query: ({ studentId, testId }) => ({
                url: `v1/${locale}/students/${studentId}/tests/${testId}`,
                method: "GET",
            }),
        }),
        testsByProfessor: builder.mutation({
            query: (body) => {
                let url = `v1/${locale}/tests`;

                for (const property in body) {
                    if ((property && body[property]) || (property === "startPage" && body[property] === 0)) {
                        if (url?.includes("?")) {
                            url += `&${property}=${body[property]}`;
                        } else {
                            url += `?${property}=${body[property]}`;
                        }
                    }
                }

                return {
                    url: url,
                    method: "GET",
                };
            },
        }),
        viewTestByProfessor: builder.mutation({
            query: ({ testId }) => ({
                url: `v1/${locale}/tests/${testId}`,
                method: "GET",
            }),
        }),
        addTest: builder.mutation({
            query: ({ body, studentId }) => ({
                url: `v1/${locale}/students/${studentId}/tests`,
                method: "POST",
                body: body,
            }),
        }),
        updateTest: builder.mutation({
            query: ({ body, studentId, testId }) => ({
                url: `v1/${locale}/students/${studentId}/tests/${testId}`,
                method: "PATCH",
                body: body,
            }),
        }),
        deleteTest: builder.mutation({
            query: ({ studentId, testId }) => ({
                url: `v1/${locale}/students/${studentId}/tests/${testId}`,
                method: "DELETE",
            }),
        }),
        verifyTest: builder.mutation({
            query: ({ studentId, testId }) => ({
                url: `v1/${locale}/students/${studentId}/tests/${testId}/verify-one`,
                method: "PATCH"
            }),
        }),
        cancelTest: builder.mutation({
            query: ({ studentId, testId }) => ({
                url: `v1/${locale}/students/${studentId}/tests/${testId}/cancel`,
                method: "PATCH"
            }),
        }),
        certificatesList: builder.mutation({
            query: (studentId) => {
                let url = `v1/${locale}/students/${studentId}/certificates`;

                return {
                    url: url,
                    method: "GET",
                };
            },
        }),
        viewCertificate: builder.mutation({
            query: ({ studentId, certificateId }) => ({
                url: `v1/${locale}/students/${studentId}/certificates/${certificateId}`,
                method: "GET",
            }),
        }),
        addCertificate: builder.mutation({
            query: ({ body, studentId }) => ({
                url: `v1/${locale}/students/${studentId}/certificates`,
                method: "POST",
                body: body,
            }),
        }),
        deleteCertificate: builder.mutation({
            query: ({ studentId, certificateId }) => ({
                url: `v1/${locale}/students/${studentId}/certificates/${certificateId}`,
                method: "DELETE",
            }),
        }),
        contractsList: builder.mutation({
            query: ({ studentId }) => {
                let url = `v1/${locale}/students/${studentId}/contracts`;

                return {
                    url: url,
                    method: "GET",
                };
            },
        }),
        addContract: builder.mutation({
            query: ({ body, studentId }) => ({
                url: `v1/${locale}/students/${studentId}/contracts`,
                method: "POST",
                body: body,
            }),
        }),
        editContract: builder.mutation({
            query: ({ body, studentId, contractId }) => ({
                url: `v1/${locale}/students/${studentId}/contracts/${contractId}`,
                method: "PATCH",
                body: body,
            }),
        }),
        deleteContract: builder.mutation({
            query: ({ studentId, contractId }) => ({
                url: `v1/${locale}/students/${studentId}/contracts/${contractId}`,
                method: "DELETE",
            }),
        }),
        resendContract: builder.mutation({
            query: ({ studentId, contractId }) => ({
                url: `v1/${locale}/students/${studentId}/contracts/${contractId}`,
                method: "POST",
            }),
        }),
        viewContract: builder.mutation({
            query: ({ studentId, contractId }) => ({
                url: `v1/${locale}/students/${studentId}/contracts/${contractId}`,
                method: "GET",
            }),
        }),
        signedContract: builder.mutation({
            query: ({ studentId, contractId }) => ({
                url: `v1/${locale}/students/${studentId}/contracts/${contractId}/signed`,
                method: "PATCH",
            }),
        }),
        archiveContract: builder.mutation({
            query: ({ studentId, contractId }) => ({
                url: `v1/${locale}/students/${studentId}/contracts/${contractId}/archive`,
                method: "PATCH",
            }),
        }),
        exportStudent: builder.mutation({
            query: () => ({
                url: `v1/${locale}/export/students`,
                method: "GET",
                responseHandler: (response) => response.blob(),
            }),
        }),
        exportStudentTests: builder.mutation({
            query: () => ({
                url: `v1/${locale}/export/tests`,
                method: "GET",
                responseHandler: (response) => response.blob(),
            }),
        }),
    }),
});

export const { 
    useStudentsListMutation, 
    useDeleteStudentsMutation, 
    useAddStudentsMutation, 
    useViewStudentsMutation, 
    useUpdateStudentsMutation, 
    useAddAvatarMutation,
    useViewAvatarMutation,
    useDeleteAvatarMutation,
    useAddDocumentMutation,
    useListDocumentsMutation,
    useDeleteDocumentMutation,
    useDownloadDocumentMutation,
    useNotesListMutation,
    useViewNoteMutation,
    useAddNoteMutation,
    useDeleteNoteMutation,
    useTestsListMutation, 
    useTestsByProfessorMutation,
    useViewTestByProfessorMutation,
    useAddTestMutation, 
    useDeleteTestMutation, 
    useViewTestMutation,
    useVerifyTestMutation,
    useCancelTestMutation,
    useCertificatesListMutation,
    useAddCertificateMutation,
    useDeleteCertificateMutation,
    useViewCertificateMutation,
    useAddContractMutation,
    useDeleteContractMutation,
    useEditContractMutation,
    useContractsListMutation,
    useSignedContractMutation,
    useViewContractMutation,
    useResendContractMutation,
    useArchiveContractMutation,
    useExportStudentMutation,
    useExportStudentTestsMutation
} = studentsManagement;
